import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { bubbleImageSrc, cargoMediaSrc } from '@/lib/bubbleImage';
import { Wind, Sparkle, Moon, Package, ArrowRight, Heart } from '@phosphor-icons/react';
import EncounterScene from '@/components/EncounterScene';

const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const m = (Date.now() - d.getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

const Header = () => (
  <div className="mb-8">
    <div className="text-[10px] uppercase tracking-widest text-bx-cyan">layer 4 · what happened</div>
    <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Your Bubble's Diary</h1>
    <p className="text-white/60 mt-2 max-w-xl text-sm">
      Encounters, drifts, dreams. While you weren't watching, your bubbles were alive.
    </p>
  </div>
);

const IncomingDrift = ({ d, onOpened }) => {
  const [opening, setOpening] = useState(false);
  const [scene, setScene] = useState(null);

  const open = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const { data } = await api.post(`/drifts/${d.id}/open`);
      setScene(data.scene);
      onOpened?.();
    } finally { setOpening(false); }
  };

  if (scene) {
    return (
      <div className="my-4">
        <EncounterScene scene={scene} canReact onReact={onOpened} />
      </div>
    );
  }

  return (
    <motion.button
      data-testid={`incoming-drift-${d.id}`}
      onClick={open}
      whileHover={{ y: -2 }}
      className="w-full glass rounded-2xl p-5 text-left flex items-center gap-4 border border-bx-magenta/30 hover:border-bx-magenta/60 transition"
    >
      <div className="h-12 w-12 rounded-full bg-bx-magenta/20 grid place-items-center">
        <Wind size={20} weight="duotone" className="text-bx-magenta" />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-bx-magenta">a bubble drifted to you</div>
        <div className="font-display font-bold">
          {d.bubble_name} arrived from @{d.owner_username}
        </div>
        <div className="text-xs text-white/65 mt-0.5">
          carrying a {d.cargo_kind} · {fmtAgo(d.landed_at || d.created_at)}
        </div>
      </div>
      <span className="text-[11px] uppercase tracking-widest text-bx-magenta font-bold">
        {opening ? 'opening…' : 'open'} <ArrowRight size={12} className="inline" />
      </span>
    </motion.button>
  );
};

const SceneCard = ({ s }) => (
  <div className="my-3">
    <EncounterScene scene={s} canReact={false} />
    <div className="text-[10px] text-white/60 uppercase tracking-widest mt-2 px-2">
      {s.trigger} encounter · {fmtAgo(s.created_at)}
    </div>
  </div>
);

const DriftReturnedCard = ({ d }) => (
  <div data-testid={`drift-returned-${d.id}`} className="glass rounded-2xl p-4 flex items-center gap-3 border border-bx-volt/30">
    <div className="h-10 w-10 rounded-full bg-bx-volt/20 grid place-items-center">
      <Heart size={18} weight="fill" className="text-bx-volt" />
    </div>
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-widest text-bx-volt">came home</div>
      <div className="font-bold">{d.bubble_name} returned from {d.destination_label}</div>
      <div className="text-[10px] text-white/60">{fmtAgo(d.returned_at)}</div>
    </div>
    <Link to={`/bubble/${d.bubble_id}`} className="text-xs text-bx-cyan font-bold uppercase tracking-widest">stories →</Link>
  </div>
);

const DreamCard = ({ d }) => {
  const imgSrc = bubbleImageSrc(d);
  return (
    <div className="glass rounded-2xl p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-full overflow-hidden bg-white/5 grid place-items-center">
        {imgSrc ? <img src={imgSrc} alt="" className="h-full w-full object-cover" /> : <Moon size={18} className="text-bx-cyan" />}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan">dream</div>
        <div className="text-sm text-white/80 italic">"{d.title || d.description || 'I dreamed something iridescent.'}"</div>
        <div className="text-[10px] text-white/60">{fmtAgo(d.created_at)}</div>
      </div>
    </div>
  );
};

const Empty = () => (
  <div data-testid="encounters-empty" className="glass rounded-3xl p-10 text-center">
    <div className="mx-auto h-16 w-16 rounded-full bg-white/5 grid place-items-center mb-4">
      <Sparkle size={28} className="text-bx-cyan" />
    </div>
    <div className="font-display text-xl font-bold mb-2">Nothing has happened yet.</div>
    <p className="text-white/65 text-sm max-w-md mx-auto">
      Your bubbles haven't been out in the world.
      Give one of them something to carry, then send it drifting.
    </p>
    <Link to="/bubbles" className="inline-block mt-5 btn-volt px-6 py-3 rounded-full font-bold text-sm">
      Go to your bubbles
    </Link>
  </div>
);

const Encounters = () => {
  const [data, setData] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [enc, inc] = await Promise.all([
        api.get('/encounters/mine'),
        api.get('/drifts/incoming'),
      ]);
      setData(enc.data);
      setIncoming(inc.data.drifts || []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <Header />

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : (
        <>
          {incoming.length > 0 && (
            <section className="mb-6">
              <div className="text-[10px] uppercase tracking-widest text-bx-magenta mb-2">inbox · {incoming.length}</div>
              <div className="space-y-3">
                {incoming.filter((d) => d.status === 'landed').map((d) => (
                  <IncomingDrift key={d.id} d={d} onOpened={load} />
                ))}
              </div>
            </section>
          )}

          {(data?.encounters || []).length === 0 && incoming.filter((d) => d.status === 'landed').length === 0 ? (
            <Empty />
          ) : (
            <div className="space-y-3">
              {(data?.encounters || []).map((e, i) => {
                if (e.kind === 'scene') return <SceneCard key={`s-${e.payload.id}`} s={e.payload} />;
                if (e.kind === 'drift_returned') return <DriftReturnedCard key={`r-${e.payload.id}`} d={e.payload} />;
                if (e.kind === 'drift_incoming') {
                  if (e.payload.status === 'opened') {
                    // already opened — just show as a meeting card via stories
                    return (
                      <div key={`o-${e.payload.id}`} className="glass rounded-2xl p-4 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-bx-magenta/20 grid place-items-center">
                          <Package size={18} className="text-bx-magenta" />
                        </div>
                        <div className="flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-bx-magenta">opened a drift</div>
                          <div className="text-sm">From @{e.payload.owner_username} — {e.payload.bubble_name}</div>
                          <div className="text-[10px] text-white/60">{fmtAgo(e.payload.opened_at)}</div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }
                if (e.kind === 'dream') return <DreamCard key={`d-${e.payload.id || i}`} d={e.payload} />;
                return null;
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Encounters;
